'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import useUploadImages from '@/hooks/useUploadImages';
import { cn } from '@/lib/utils';

const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 20;

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function MultiImageUploader({
  open,
  setOpen,
  onSuccess,
  onCancel,
}: Props) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { mutate: uploadImages, isPending: uploadPending } = useUploadImages();

  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  // Centralized file processing logic
  const processFiles = (files: FileList | null) => {
    setError(null);
    if (!files) return;

    if (selectedFiles.length + files.length > MAX_FILES) {
      setError(`You can only upload a maximum of ${MAX_FILES} images.`);
      return;
    }

    const newFiles: File[] = Array.from(files);
    const newUrls: string[] = [];
    let fileError = false;

    for (const file of newFiles) {
      const isOkType = /^image\/(png|jpe?g|webp)$/i.test(file.type);
      if (!isOkType) {
        setError('Please select only PNG, JPG, or WEBP images.');
        fileError = true;
        break;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`Files must be smaller than ${MAX_FILE_SIZE_MB}MB.`);
        fileError = true;
        break;
      }
      newUrls.push(URL.createObjectURL(file));
    }

    if (fileError) {
      newUrls.forEach((url) => URL.revokeObjectURL(url));
      return;
    }

    setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    setPreviewUrls((prevUrls) => [...prevUrls, ...newUrls]);
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    processFiles(e.target.files);
    e.target.value = ''; // Reset input
  };

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      processFiles(files);
    },
    [selectedFiles],
  );

  const handleRemoveImage = (index: number) => {
    const urlToRemove = previewUrls[index];
    if (urlToRemove) URL.revokeObjectURL(urlToRemove);
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setPreviewUrls((prevUrls) => prevUrls.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    previewUrls.forEach((url) => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviewUrls([]);
    setError(null);
    setOpen(false);
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append('files', file));

    uploadImages(formData, {
      onSuccess: () => {
        toast.success(`${selectedFiles.length} image(s) uploaded successfully`);
        handleCancel();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(`Failed to upload: ${error.message}`);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Images</DialogTitle>
          <DialogDescription>
            Select or drag and drop your images below.
          </DialogDescription>
          <DialogDescription>
            In order to automatically compare your home to the comparables we
            need some photos of your home. Upload up to ten images, and be sure
            to include a picture of the front of your home, the backyard, the
            kitchen, the great room (aka family or living)and primary bathroom
            plus any others you think would be helpful.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex items-center justify-center w-full"
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <label
            htmlFor="multi-image-upload"
            className={cn(
              'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-gray-300 bg-gray-50 hover:bg-gray-100',
            )}
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Upload
                className={cn(
                  'w-8 h-8 mb-2',
                  isDragging ? 'text-primary' : 'text-gray-500',
                )}
              />
              <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">Click to upload</span> or drag
                and drop
              </p>
              <p className="text-xs text-gray-500">
                PNG, JPG or WEBP (MAX. {MAX_FILE_SIZE_MB}MB)
              </p>
            </div>
            <input
              id="multi-image-upload"
              type="file"
              accept="image/png, image/jpeg, image/webp"
              className="hidden"
              multiple
              onChange={handleFileChange}
            />
          </label>
        </div>

        {previewUrls.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">
              Selected Images ({selectedFiles.length}/{MAX_FILES})
            </h4>
            <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto p-2 border rounded-md">
              {previewUrls.map((url, index) => (
                <div key={url} className="relative aspect-square">
                  <img
                    src={url}
                    alt="Preview"
                    className="w-full h-full object-cover rounded-md"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full"
                    onClick={() => handleRemoveImage(index)}
                    disabled={uploadPending}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-600 font-medium">{error}</p>}

        <DialogFooter className="flex justify-between">
          <Button
            disabled={uploadPending}
            variant="outline"
            type="button"
            onClick={() => {
              handleCancel();
              onCancel?.();
            }}
          >
            Skip the AI analysis
          </Button>
          <Button
            disabled={uploadPending || selectedFiles.length === 0}
            type="button"
            onClick={handleUpload}
          >
            {uploadPending
              ? 'Uploading...'
              : `Upload ${selectedFiles.length} Image(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
